import { MultiPoint } from '@ikegps/core-measurement';
import type { DistanceBearingPitch } from '@ikegps/core-api';
import { type CollectionModelFieldAnchor, CollectionModelFieldVector } from '@ikegps/core-shared';
import { isEqual } from 'lodash';
import { AnchorInput } from '../../../components/input/anchor.input';
import { AnchorInputState } from '../../../components/input/anchor.input.state';
import { Input } from '../../../components/input/input';
import { T, Translate } from '../../../i18n';
import { m, type MithrilVNode } from '../../../mithril';
import { CurrentState } from '../../../state/page.state';
import { FieldInfo, type FieldStat } from '../../field.info/field.info';
import { TopologicalPointCollectionCache } from '../../topological.editor.map/point.collection.cache';
import type { ICollectionViewController } from '../collection.view.ctrl.interface';
import type { CollectionFieldView } from './field.view';

export function viewAnchorManualEdit(field: CollectionModelFieldAnchor, ctrl: ICollectionViewController): MithrilVNode {
	let inputState = ctrl.changeState.getState(field.id) as AnchorInputState;

	// convert GPS to vector
	const source = TopologicalPointCollectionCache.getCollectionLocation(field.collection.revision);
	const distanceBearingPitch: DistanceBearingPitch = field.distanceBearingPitch(source);

	if (!inputState) {
		const { value } = field;

		inputState = new AnchorInputState(field);
		inputState.onValue(() => {
			// A display refresh (e.g. after a pole move) must not convert an accurate GPS anchor into a
			// manual one; only edits that diverge from the reflected value persist (UFO-720).
			if (inputState.isUnchangedFromReflectedModel()) {
				return;
			}

			field.Mutator.manual(inputState.value);

			// This will ensure topo map is updated (if it exists) and a user is updating vector locations
			TopologicalPointCollectionCache.dropCollectionVectors(field.collection.revision);
		});

		ctrl.changeState.addState(field.id, inputState);

		if (value != null && distanceBearingPitch != null) {
			inputState.syncFromModel({
				distance: distanceBearingPitch.distance,
				bearing: distanceBearingPitch.bearing,
				pitch: distanceBearingPitch.pitch,
				placement: value.placement,
				relatedSpanIds: value.relatedSpanIds,
				collectionTool: 'VECTOR',
			});
		}
	}

	const linkableSpanFields: CollectionModelFieldVector[] = [];

	field.collection.forEachField((x) => {
		if (CollectionModelFieldVector.isVectorField(x)) {
			linkableSpanFields.push(x);
		}
	});

	if (
		!isEqual(
			// Pre-existing: subtracting field objects yields NaN (no real ordering); preserved as-is.
			linkableSpanFields.sort((a, b) => (a as unknown as number) - (b as unknown as number)),
			inputState.cachedSpanFields
		)
	) {
		inputState.populateRelatedSpansOptions(linkableSpanFields);
	}

	// If none of the inputs are focused, and the field model value changes
	// the input state should also update.
	if (
		field.value !== null &&
		distanceBearingPitch !== null &&
		!(
			inputState.distance.meters.focused ||
			inputState.distance.inch.focused ||
			inputState.distance.feet.focused ||
			inputState.bearing.focused
		)
	) {
		inputState.syncFromModel({
			...distanceBearingPitch,
			placement: inputState.placement.value,
			relatedSpanIds: inputState.relatedSpans.map((x) => x.value),
			collectionTool: 'VECTOR',
		});
	}

	const onmouseover = () => {
		CurrentState.setHoverId(field.id);
	};
	const onmouseout = () => {
		CurrentState.setHoverId(null);
	};
	const className = Input.CSS.container;

	return m('div', { className, onmouseover, onmouseout }, [AnchorInput.view(inputState, ctrl, field)]);
}

export const AnchorManualFieldView: CollectionFieldView<CollectionModelFieldAnchor> = {
	hasClear() {
		return false;
	},

	viewInline(field: CollectionModelFieldAnchor): MithrilVNode | null {
		if (!field.hasValue()) {
			return null;
		}
		// convert location to distance/bearing
		const source = TopologicalPointCollectionCache.getCollectionLocation(field.collection.revision);
		if (!source) {
			return null;
		}
		const distanceBearingPitch: DistanceBearingPitch = field.distanceBearingPitch(source);
		const onmouseover = () => {
			CurrentState.setHoverId(field.id);
		};
		const onmouseout = () => {
			CurrentState.setHoverId(null);
		};
		const className = Input.CSS.container;

		return m('div', { className, onmouseover, onmouseout }, [
			`${FieldInfo.viewVectorInline(distanceBearingPitch)}, Alignment: ${Translate(field.value.placement)}`,
		]);
	},

	hasDetail() {
		return false;
	},

	viewDetail(): MithrilVNode {
		return null;
	},

	viewEdit(field: CollectionModelFieldAnchor, ctrl: ICollectionViewController): MithrilVNode | string {
		return viewAnchorManualEdit(field, ctrl);
	},
};

export const AnchorP2PFieldView: CollectionFieldView<CollectionModelFieldAnchor> = {
	viewInline(field: CollectionModelFieldAnchor): MithrilVNode | null {
		if (!field.hasValue()) {
			return null;
		}
		// convert location to distance/bearing
		const source = TopologicalPointCollectionCache.getCollectionLocation(field.collection.revision);
		if (!source) {
			return null;
		}
		const distanceBearingPitch = field.distanceBearingPitch(source);
		if (distanceBearingPitch == null) {
			return null;
		}
		const onmouseover = () => {
			CurrentState.setHoverId(field.id);
		};
		const onmouseout = () => {
			CurrentState.setHoverId(null);
		};
		const className = Input.CSS.container;

		return m('div', { className, onmouseover, onmouseout }, [
			`${FieldInfo.viewVectorInline(distanceBearingPitch)}, Alignment: ${Translate(field.value.placement)}`,
		]);
	},

	hasDetail() {
		return true;
	},

	viewDetail(field: CollectionModelFieldAnchor): MithrilVNode {
		const collection = field.collection;
		const captures = field.getCaptures();
		const p2pMeasurement = MultiPoint.Points(captures);

		const stats: FieldStat[] = [
			{ name: T.DISTANCE_VERTICAL, value: Math.abs(p2pMeasurement.vertical) },
			{ name: T.DISTANCE_HORIZONTAL, value: p2pMeasurement.horizontal },
		];

		return FieldInfo.viewCaptures(collection, captures, stats);
	},

	viewEdit(field: CollectionModelFieldAnchor, ctrl: ICollectionViewController): MithrilVNode {
		return viewAnchorManualEdit(field, ctrl);
	},
};
